import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { NotificationService } from 'app/_services/notification.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { SiteService } from 'app/_services/site.service';
import { environment } from 'environments/environment';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { SortEvent } from 'primeng/api';

@Component({
  selector: 'app-site-assets',
  templateUrl: './site-assets.component.html',
})
export class SiteAssetsComponent implements OnInit {
  private selectedSiteId: any;
  public selectedSite: any;

  public siteAssets: any = [];
  public selectedAsset: any;

  private routeRole: string;

  public columnOptionsSidebarVisible: boolean;
  public canAddAssets = false;

  public togglableColumns = [
    { displayName: 'Description', default: true },
    { displayName: 'Manufacturer - Model - Rating', default: true },
    { displayName: 'Restrictions', default: true },
    { displayName: 'Certificates', default: true },
    { displayName: 'Categories', default: true },
  ];

  // table options
  public tableOptions = {
    visibleColumns: [],
  }

  // pagination
  public pageSizes: number[] = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator: any;

  // Sorting and Filtering
  public defaultSortField = 'name';
  public selectedSort = this.defaultSortField + ',asc';

  public isFilterLoading: boolean;
  public assetFilterName = '';

  constructor(
    private siteService: SiteService,
    private siteAssetService: SiteAssetsService,
    private router: Router,
    private globals: Globals,
    activatedRoute: ActivatedRoute,
    public notificationService: NotificationService,
  ) {
    // Get SiteId from Params
    activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.routeRole = globals.user.routeRole;
  }

  ngOnInit() {
    this.setupColumnOptions();
    this.getSelectedSite();
    this.resetPagination(); // call service to populate table
  }

  // Get Selected Site
  public getSelectedSite() {
    this.siteService.getSiteById(this.selectedSiteId).subscribe((data: any) => {
      this.selectedSite = data;

      this.checkAddingAssetPermission();
    });
  }

  public checkAddingAssetPermission() {
    if (this.routeRole === 'site-owner') {
      this.canAddAssets = this.globals.siteOwnerToggles.addAssets;
    } else if (this.routeRole === 'site-delegate') {
      this.canAddAssets = this.selectedSite.siteOwner.siteOwnerUser.addAssets;
    } else {
      this.canAddAssets = true;
    }
  }

  public getPaginatedSiteAssets() {
    this.siteAssetService.getAllSiteAssets(this.selectedSiteId, this.pagination, this.selectedSort, this.assetFilterName).subscribe(
      (data: any) => {
        this.siteAssets = data.body;
        this.isFilterLoading = false;

        this.siteAssets.forEach(asset => {
          asset.assetCertificates = asset.assetCertificates.filter((cert) => !cert.deleted);
          asset.manufacturerModelRatingDisplay = this.buildManufacturerModelRatingDisplay(asset);

          asset.assetCertificates
            .sort((a, b) => {
              return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
            })
            .forEach(certificate => {
              certificate.expired = DateTimeUtils.isDateStrExpired(certificate.expiryDate);
            });
        });

        this.pagination.totalRecords = Number(data.headers.get('X-Total-Count'));
      }, error => {
        console.error(error);
        this.notificationService.notify('danger', 'Error retrieving asset list');
        this.isFilterLoading = false;
      });
  }

  // Handle Pagination
  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getPaginatedSiteAssets();
  }

  public resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
    this.getPaginatedSiteAssets();
  }

  public customSort(sort: SortEvent) {
    let sortDirection: String = 'asc';
    let sortOrder = sort.order;

    // reverse default order to put locked and non-empty restrictions on top
    if (sort.field === 'locked' || sort.field === 'restrictions') {
      sortOrder = sortOrder * -1;
    }

    if (sortOrder === -1) {
      sortDirection = 'desc';
    }

    // main sort then, sub-sort by name
    this.selectedSort = sort.field + ',' + sortDirection + '&sort=name,asc';

    this.goToFirstPage();

    this.getPaginatedSiteAssets();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public addAsset() {
    if (!this.canAddAssets) {
      return;
    }

    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/indi-asset', { type: 'add' }]);
  }

  public async onRowSelect(event) {
    this.selectedAsset = event.data;

    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.selectedAsset.id]);
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.assets', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.assets');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);

      // Ensure newly added default-on columns are enabled for existing saved preferences.
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));

      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }

  public onFilterTextChanged(filterText: string){
    this.isFilterLoading = true;
    this.assetFilterName = filterText;
    this.goToFirstPage();
    this.getPaginatedSiteAssets();
  }

  private buildManufacturerModelRatingDisplay(asset: any): string {
    const details: string[] = [];
    if (asset.manufacturer) {
      details.push(asset.manufacturer);
    }
    if (asset.model) {
      details.push(asset.model);
    }
    if (asset.rating) {
      details.push(`${asset.rating} kW`);
    }

    return details.join(' - ');
  }
}
